import { initializeApp } from "firebase/app";
import {
  getAnalytics,
  isSupported as isAnalyticsSupported,
} from "firebase/analytics";
import {
  deleteToken,
  getMessaging,
  getToken,
  isSupported as isMessagingSupported,
  onMessage,
} from "firebase/messaging";

export const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
  measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID,
};

// Initialize Firebase app once and share across the app.
const app = initializeApp(firebaseConfig);

// Analytics is optional; guard for environments where it's unsupported.
let analytics = null;

if (typeof window !== "undefined") {
  isAnalyticsSupported()
    .then((supported) => {
      if (supported && firebaseConfig?.measurementId) {
        analytics = getAnalytics(app);
      }
    })
    .catch((error) => {
      console.warn("Firebase analytics unavailable", error);
    });
}

let messaging = null;
let messagingInitPromise = null;

async function initMessagingIfSupported() {
  if (typeof window === "undefined") return null;
  if (!("serviceWorker" in navigator)) return null;
  if (!("Notification" in window)) return null;

  const supported = await isMessagingSupported().catch(() => false);
  if (!supported) return null;

  return getMessaging(app);
}

/**
 * Get Firebase Messaging instance if supported in the current browser.
 * Returns `null` if unsupported (e.g. Safari without web-push, no SW, etc).
 */
export async function getMessagingInstance() {
  if (messaging) return messaging;
  if (!messagingInitPromise) {
    messagingInitPromise = initMessagingIfSupported().then((m) => {
      messaging = m;
      return m;
    });
  }
  return await messagingInitPromise;
}

/**
 * Ask browser notification permission (returns "granted" | "denied" | "default").
 */
export async function requestPushPermission() {
  if (typeof window === "undefined") return "denied";
  if (!("Notification" in window)) return "denied";
  if (Notification.permission === "denied") return "denied";
  return await Notification.requestPermission();
}

/**
 * Get (or create) an FCM token for this browser.
 *
 * IMPORTANT: pass the PWA service worker registration so FCM uses the same SW scope.
 * `registration` can be obtained via `await navigator.serviceWorker.ready`.
 */
export async function getFcmToken({ registration } = {}) {
  const msg = await getMessagingInstance();
  if (!msg) return null;

  const permission =
    Notification.permission === "granted"
      ? "granted"
      : await requestPushPermission();
  if (permission !== "granted") return null;

  const vapidKey = import.meta.env.VITE_FIREBASE_VAPID_KEY_PAIR;
  if (!vapidKey) {
    throw new Error(
      "Missing env: VITE_FIREBASE_VAPID_KEY_PAIR (Web Push certificates key pair)",
    );
  }

  return await getToken(msg, {
    vapidKey,
    ...(registration ? { serviceWorkerRegistration: registration } : {}),
  });
}

/**
 * Delete the current FCM token (useful for logout / opt-out).
 */
export async function deleteFcmToken(token) {
  const msg = await getMessagingInstance();
  if (!msg) return false;
  return await deleteToken(msg, token);
}

/**
 * Listen to foreground push messages (while the page is open).
 * Returns an unsubscribe function.
 */
export async function onForegroundMessage(handler) {
  const msg = await getMessagingInstance();
  if (!msg) return () => {};
  return onMessage(msg, handler);
}

export { app, analytics, messaging };
